import { MessageTokenUsage } from '../services/tokenTracker'
export interface User {
  id: string
  name: string
  role: string
  avatar: string
  initials: string
  workload: number
}
export interface Issue {
  key: string
  summary: string
  type: 'Story' | 'Bug' | 'Task' | 'Epic'
  status: 'To Do' | 'In Progress' | 'Review' | 'Done' | 'Backlog'
  /** Original Jira status name (e.g. "Selected for Development", "Pendiente de implementación") */
  rawStatus?: string
  assignee?: User
  points?: number
  priority: 'High' | 'Medium' | 'Low'
  labels?: string[]
  createdDate?: string
}
export interface Sprint {
  id: string
  name: string
  goal: string
  startDate: string
  endDate: string
  status: 'Active' | 'Planned' | 'Completed'
  progress: number
  totalPoints: number
  completedPoints: number
}
export interface KPI {
  id: string
  label: string
  value: string | number
  trend: 'up' | 'down' | 'neutral'
  trendValue: string
  description: string
}
export interface ChatMessage {
  id: string
  role: 'user' | 'agent'
  content: string
  timestamp: string
  suggestedFollowups?: string[]
  mcpCalls?: string[]
  latencyMs?: number
  intentConfidence?: number
  warnings?: string[]
  feedback?: 'up' | 'down' | null
  attachments?: {
    type: 'kpi' | 'issues' | 'sprint_summary' | 'blockers' | 'decision'
    data: any
  }[]
  artifacts?: any[] // ChatArtifact from artifactDetector
  tokenUsage?: MessageTokenUsage
}

// ─── Multi-Project ────────────────────────────────────────────────────────────
export interface Project {
  id: string
  key: string
  name: string
  description: string
  color: string // tailwind color class
  colorHex: string
  status: 'active' | 'at-risk' | 'on-hold' | 'completed'
  team: string
  teamSize: number
  velocity: number // avg story points per sprint
  completion: number // % sprint completion
  issues: Issue[]
  backlog: Issue[]
  sprint: Sprint
  sprints?: Sprint[] // All sprints (active, planned, completed)
  kpis: KPI[]
}

// ─── Users ────────────────────────────────────────────────────────────────────
export const users: User[] = [
  {
    id: 'u1',
    name: 'Ana García',
    role: 'Product Owner',
    avatar: '',
    initials: 'AG',
    workload: 40,
  },
  {
    id: 'u2',
    name: 'Carlos Ruiz',
    role: 'Lead Dev',
    avatar: '',
    initials: 'CR',
    workload: 95,
  },
  {
    id: 'u3',
    name: 'Sofia Chen',
    role: 'Frontend Dev',
    avatar: '',
    initials: 'SC',
    workload: 80,
  },
  {
    id: 'u4',
    name: 'Miguel Silva',
    role: 'Backend Dev',
    avatar: '',
    initials: 'MS',
    workload: 65,
  },
  {
    id: 'u5',
    name: 'Laura Park',
    role: 'QA Engineer',
    avatar: '',
    initials: 'LP',
    workload: 50,
  },
  {
    id: 'u6',
    name: 'Diego Torres',
    role: 'Mobile Dev',
    avatar: '',
    initials: 'DT',
    workload: 75,
  },
  {
    id: 'u7',
    name: 'Valentina Ríos',
    role: 'iOS Dev',
    avatar: '',
    initials: 'VR',
    workload: 60,
  },
  {
    id: 'u8',
    name: 'Mateo López',
    role: 'DevOps',
    avatar: '',
    initials: 'ML',
    workload: 85,
  },
]

// ─── Project: VSM (Workium AI Platform) ────────────────────────────────────────
const vsmIssues: Issue[] = [
  {
    key: 'VSM-101',
    summary: 'Implementar autenticación con Cognito',
    type: 'Story',
    status: 'In Progress',
    assignee: users[3],
    points: 5,
    priority: 'High',
    labels: ['auth', 'backend'],
  },
  {
    key: 'VSM-102',
    summary: 'Diseñar dashboard de KPIs',
    type: 'Story',
    status: 'Review',
    assignee: users[2],
    points: 8,
    priority: 'High',
    labels: ['frontend', 'ui'],
  },
  {
    key: 'VSM-103',
    summary: 'Error en cálculo de velocidad',
    type: 'Bug',
    status: 'To Do',
    assignee: users[1],
    points: 3,
    priority: 'Medium',
    labels: ['bug', 'metrics'],
  },
  {
    key: 'VSM-104',
    summary: 'Configurar pipeline CI/CD',
    type: 'Task',
    status: 'Done',
    assignee: users[1],
    points: 5,
    priority: 'High',
    labels: ['devops'],
  },
  {
    key: 'VSM-105',
    summary: 'Redactar documentación de API',
    type: 'Task',
    status: 'To Do',
    assignee: users[0],
    points: 2,
    priority: 'Low',
    labels: ['docs'],
  },
  {
    key: 'VSM-106',
    summary: 'Optimizar consultas de base de datos',
    type: 'Story',
    status: 'In Progress',
    assignee: users[3],
    points: 5,
    priority: 'Medium',
    labels: ['backend', 'performance'],
  },
]
const vsmBacklog: Issue[] = [
  {
    key: 'VSM-201',
    summary: 'Integración con Slack para notificaciones',
    type: 'Story',
    status: 'Backlog',
    points: 8,
    priority: 'High',
    labels: ['integration', 'slack'],
    createdDate: '2024-02-15',
  },
  {
    key: 'VSM-202',
    summary: 'Dashboard de reportes ejecutivos',
    type: 'Epic',
    status: 'Backlog',
    points: 21,
    priority: 'High',
    labels: ['frontend', 'reporting'],
    createdDate: '2024-02-10',
  },
  {
    key: 'VSM-203',
    summary: 'Exportar datos a CSV/Excel',
    type: 'Story',
    status: 'Backlog',
    assignee: users[2],
    points: 5,
    priority: 'Medium',
    labels: ['export', 'data'],
    createdDate: '2024-02-20',
  },
  {
    key: 'VSM-204',
    summary: 'Modo offline con sincronización',
    type: 'Story',
    status: 'Backlog',
    points: 13,
    priority: 'Low',
    labels: ['offline', 'sync'],
    createdDate: '2024-02-18',
  },
  {
    key: 'VSM-205',
    summary: 'Integración con GitHub Actions',
    type: 'Task',
    status: 'Backlog',
    assignee: users[1],
    points: 5,
    priority: 'Medium',
    labels: ['devops', 'github'],
    createdDate: '2024-02-22',
  },
  {
    key: 'VSM-206',
    summary: 'Sistema de permisos por rol',
    type: 'Story',
    status: 'Backlog',
    points: 13,
    priority: 'High',
    labels: ['auth', 'security'],
    createdDate: '2024-02-12',
  },
  {
    key: 'VSM-207',
    summary: 'Notificaciones push en el navegador',
    type: 'Story',
    status: 'Backlog',
    points: 5,
    priority: 'Low',
    labels: ['notifications', 'frontend'],
    createdDate: '2024-02-25',
  },
  {
    key: 'VSM-208',
    summary: 'API rate limiting y throttling',
    type: 'Task',
    status: 'Backlog',
    assignee: users[3],
    points: 3,
    priority: 'Medium',
    labels: ['backend', 'security'],
    createdDate: '2024-03-01',
  },
  {
    key: 'VSM-209',
    summary: 'Tema personalizable por usuario',
    type: 'Story',
    status: 'Backlog',
    points: 8,
    priority: 'Low',
    labels: ['frontend', 'ux'],
    createdDate: '2024-03-02',
  },
  {
    key: 'VSM-210',
    summary: 'Migración a microservicios',
    type: 'Epic',
    status: 'Backlog',
    points: 34,
    priority: 'Medium',
    labels: ['architecture', 'backend'],
    createdDate: '2024-01-15',
  },
]

// ─── Project: MOBILE (App Móvil) ─────────────────────────────────────────────
const mobileIssues: Issue[] = [
  {
    key: 'MOB-001',
    summary: 'Pantalla de onboarding con animaciones',
    type: 'Story',
    status: 'In Progress',
    assignee: users[5],
    points: 8,
    priority: 'High',
    labels: ['ui', 'animation'],
  },
  {
    key: 'MOB-002',
    summary: 'Push notifications iOS y Android',
    type: 'Story',
    status: 'In Progress',
    assignee: users[6],
    points: 5,
    priority: 'High',
    labels: ['notifications', 'mobile'],
  },
  {
    key: 'MOB-003',
    summary: 'Crash en pantalla de perfil (iOS 17)',
    type: 'Bug',
    status: 'To Do',
    assignee: users[6],
    points: 3,
    priority: 'High',
    labels: ['bug', 'ios'],
  },
  {
    key: 'MOB-004',
    summary: 'Integración con biometría',
    type: 'Story',
    status: 'Done',
    assignee: users[5],
    points: 5,
    priority: 'Medium',
    labels: ['auth', 'biometric'],
  },
  {
    key: 'MOB-005',
    summary: 'Dark mode en toda la app',
    type: 'Task',
    status: 'Review',
    assignee: users[5],
    points: 8,
    priority: 'Medium',
    labels: ['ui', 'theme'],
  },
  {
    key: 'MOB-006',
    summary: 'Optimizar tamaño del bundle',
    type: 'Task',
    status: 'To Do',
    assignee: users[5],
    points: 3,
    priority: 'Low',
    labels: ['performance'],
  },
]
const mobileBacklog: Issue[] = [
  {
    key: 'MOB-101',
    summary: 'Widget para pantalla de inicio',
    type: 'Story',
    status: 'Backlog',
    points: 13,
    priority: 'High',
    labels: ['widget', 'ios'],
    createdDate: '2024-02-20',
  },
  {
    key: 'MOB-102',
    summary: 'Soporte para tablets',
    type: 'Epic',
    status: 'Backlog',
    points: 21,
    priority: 'Medium',
    labels: ['tablet', 'responsive'],
    createdDate: '2024-02-15',
  },
  {
    key: 'MOB-103',
    summary: 'Modo offline con sync',
    type: 'Story',
    status: 'Backlog',
    points: 13,
    priority: 'High',
    labels: ['offline', 'sync'],
    createdDate: '2024-03-01',
  },
  {
    key: 'MOB-104',
    summary: 'Integración con Apple Watch',
    type: 'Story',
    status: 'Backlog',
    points: 8,
    priority: 'Low',
    labels: ['watchos'],
    createdDate: '2024-02-28',
  },
]

// ─── Project: INFRA (Infrastructure & DevOps) ─────────────────────────────────
const infraIssues: Issue[] = [
  {
    key: 'INF-001',
    summary: 'Migrar a Kubernetes EKS',
    type: 'Epic',
    status: 'In Progress',
    assignee: users[7],
    points: 13,
    priority: 'High',
    labels: ['k8s', 'aws'],
  },
  {
    key: 'INF-002',
    summary: 'Configurar alertas en CloudWatch',
    type: 'Task',
    status: 'Done',
    assignee: users[7],
    points: 3,
    priority: 'High',
    labels: ['monitoring', 'aws'],
  },
  {
    key: 'INF-003',
    summary: 'Implementar WAF para APIs',
    type: 'Story',
    status: 'Review',
    assignee: users[7],
    points: 5,
    priority: 'High',
    labels: ['security', 'waf'],
  },
  {
    key: 'INF-004',
    summary: 'Automatizar backups de RDS',
    type: 'Task',
    status: 'To Do',
    assignee: users[7],
    points: 3,
    priority: 'Medium',
    labels: ['database', 'backup'],
  },
  {
    key: 'INF-005',
    summary: 'Reducir costos de EC2 con Spot instances',
    type: 'Story',
    status: 'In Progress',
    assignee: users[7],
    points: 8,
    priority: 'Medium',
    labels: ['cost', 'ec2'],
  },
]
const infraBacklog: Issue[] = [
  {
    key: 'INF-101',
    summary: 'Migrar a multi-región',
    type: 'Epic',
    status: 'Backlog',
    points: 34,
    priority: 'High',
    labels: ['aws', 'ha'],
    createdDate: '2024-01-20',
  },
  {
    key: 'INF-102',
    summary: 'Implementar service mesh con Istio',
    type: 'Story',
    status: 'Backlog',
    points: 21,
    priority: 'Medium',
    labels: ['k8s', 'istio'],
    createdDate: '2024-02-10',
  },
  {
    key: 'INF-103',
    summary: 'Pipeline de seguridad (SAST/DAST)',
    type: 'Task',
    status: 'Backlog',
    points: 8,
    priority: 'High',
    labels: ['security', 'ci'],
    createdDate: '2024-02-25',
  },
]

// ─── Projects ─────────────────────────────────────────────────────────────────
export const projects: Project[] = [
  {
    id: 'vsm',
    key: 'VSM',
    name: 'Workium AI Platform',
    description: 'Plataforma de agentes inteligentes para gestión de proyectos',
    color: 'bg-indigo-500',
    colorHex: '#6366f1',
    status: 'active',
    team: 'Team Alpha',
    teamSize: 5,
    velocity: 42,
    completion: 87,
    issues: vsmIssues,
    backlog: vsmBacklog,
    sprint: {
      id: 'SPR-24',
      name: 'Sprint 24: Core Features',
      goal: 'Finalizar módulo de autenticación y dashboard base',
      startDate: '2024-03-10',
      endDate: '2024-03-24',
      status: 'Active',
      progress: 65,
      totalPoints: 45,
      completedPoints: 28,
    },
    sprints: [
      {
        id: 'SPR-25',
        name: 'Sprint 25: Integrations',
        goal: 'Integrar Slack, GitHub y sistema de notificaciones',
        startDate: '2024-03-25',
        endDate: '2024-04-07',
        status: 'Planned',
        progress: 0,
        totalPoints: 38,
        completedPoints: 0,
      },
      {
        id: 'SPR-24',
        name: 'Sprint 24: Core Features',
        goal: 'Finalizar módulo de autenticación y dashboard base',
        startDate: '2024-03-10',
        endDate: '2024-03-24',
        status: 'Active',
        progress: 65,
        totalPoints: 45,
        completedPoints: 28,
      },
      {
        id: 'SPR-23',
        name: 'Sprint 23: Foundation',
        goal: 'Setup inicial del proyecto y arquitectura base',
        startDate: '2024-02-25',
        endDate: '2024-03-09',
        status: 'Completed',
        progress: 100,
        totalPoints: 40,
        completedPoints: 38,
      },
      {
        id: 'SPR-22',
        name: 'Sprint 22: Discovery',
        goal: 'Research, prototipos y definición de MVP',
        startDate: '2024-02-11',
        endDate: '2024-02-24',
        status: 'Completed',
        progress: 100,
        totalPoints: 35,
        completedPoints: 35,
      },
    ],
    kpis: [
      {
        id: 'vsm-k1',
        label: 'Velocidad Promedio',
        value: '42 pts',
        trend: 'up',
        trendValue: '+12%',
        description: 'Puntos completados por sprint',
      },
      {
        id: 'vsm-k2',
        label: 'Sprint Completion',
        value: '87%',
        trend: 'down',
        trendValue: '-3%',
        description: 'Ratio de compromiso vs entrega',
      },
      {
        id: 'vsm-k3',
        label: 'Cycle Time',
        value: '3.2 días',
        trend: 'up',
        trendValue: '-0.5d',
        description: 'Tiempo promedio In Progress → Done',
      },
      {
        id: 'vsm-k4',
        label: 'Satisfacción Equipo',
        value: '4.2/5',
        trend: 'neutral',
        trendValue: '0.0',
        description: 'Encuesta semanal de salud',
      },
    ],
  },
  {
    id: 'mobile',
    key: 'MOB',
    name: 'App Móvil',
    description: 'Aplicación nativa iOS y Android para usuarios finales',
    color: 'bg-emerald-500',
    colorHex: '#10b981',
    status: 'at-risk',
    team: 'Team Mobile',
    teamSize: 3,
    velocity: 28,
    completion: 72,
    issues: mobileIssues,
    backlog: mobileBacklog,
    sprint: {
      id: 'MOB-SPR-8',
      name: 'Sprint 8: Launch Prep',
      goal: 'Preparar release candidate para App Store y Play Store',
      startDate: '2024-03-12',
      endDate: '2024-03-26',
      status: 'Active',
      progress: 48,
      totalPoints: 32,
      completedPoints: 15,
    },
    sprints: [
      {
        id: 'MOB-SPR-9',
        name: 'Sprint 9: Post-Launch',
        goal: 'Hotfixes y mejoras post-lanzamiento',
        startDate: '2024-03-27',
        endDate: '2024-04-09',
        status: 'Planned',
        progress: 0,
        totalPoints: 25,
        completedPoints: 0,
      },
      {
        id: 'MOB-SPR-8',
        name: 'Sprint 8: Launch Prep',
        goal: 'Preparar release candidate para App Store y Play Store',
        startDate: '2024-03-12',
        endDate: '2024-03-26',
        status: 'Active',
        progress: 48,
        totalPoints: 32,
        completedPoints: 15,
      },
      {
        id: 'MOB-SPR-7',
        name: 'Sprint 7: Core UX',
        goal: 'Flujos principales de usuario y navegación',
        startDate: '2024-02-26',
        endDate: '2024-03-11',
        status: 'Completed',
        progress: 100,
        totalPoints: 30,
        completedPoints: 27,
      },
    ],
    kpis: [
      {
        id: 'mob-k1',
        label: 'Velocidad Promedio',
        value: '28 pts',
        trend: 'down',
        trendValue: '-8%',
        description: 'Puntos completados por sprint',
      },
      {
        id: 'mob-k2',
        label: 'Sprint Completion',
        value: '72%',
        trend: 'down',
        trendValue: '-15%',
        description: 'Ratio de compromiso vs entrega',
      },
      {
        id: 'mob-k3',
        label: 'Crash Rate',
        value: '0.8%',
        trend: 'up',
        trendValue: '+0.3%',
        description: 'Tasa de crashes en producción',
      },
      {
        id: 'mob-k4',
        label: 'App Store Rating',
        value: '4.6/5',
        trend: 'up',
        trendValue: '+0.1',
        description: 'Rating promedio en stores',
      },
    ],
  },
  {
    id: 'infra',
    key: 'INF',
    name: 'Infrastructure',
    description: 'Cloud infrastructure, DevOps y seguridad',
    color: 'bg-amber-500',
    colorHex: '#f59e0b',
    status: 'active',
    team: 'Team DevOps',
    teamSize: 2,
    velocity: 35,
    completion: 91,
    issues: infraIssues,
    backlog: infraBacklog,
    sprint: {
      id: 'INF-SPR-12',
      name: 'Sprint 12: K8s Migration',
      goal: 'Completar migración a EKS y configurar observabilidad',
      startDate: '2024-03-08',
      endDate: '2024-03-22',
      status: 'Active',
      progress: 78,
      totalPoints: 32,
      completedPoints: 25,
    },
    sprints: [
      {
        id: 'INF-SPR-12',
        name: 'Sprint 12: K8s Migration',
        goal: 'Completar migración a EKS y configurar observabilidad',
        startDate: '2024-03-08',
        endDate: '2024-03-22',
        status: 'Active',
        progress: 78,
        totalPoints: 32,
        completedPoints: 25,
      },
      {
        id: 'INF-SPR-11',
        name: 'Sprint 11: Monitoring',
        goal: 'CloudWatch, alertas y dashboards de observabilidad',
        startDate: '2024-02-22',
        endDate: '2024-03-07',
        status: 'Completed',
        progress: 100,
        totalPoints: 28,
        completedPoints: 28,
      },
      {
        id: 'INF-SPR-10',
        name: 'Sprint 10: Security Hardening',
        goal: 'WAF, IAM policies y security groups',
        startDate: '2024-02-08',
        endDate: '2024-02-21',
        status: 'Completed',
        progress: 100,
        totalPoints: 30,
        completedPoints: 29,
      },
    ],
    kpis: [
      {
        id: 'inf-k1',
        label: 'Uptime',
        value: '99.97%',
        trend: 'up',
        trendValue: '+0.02%',
        description: 'Disponibilidad del sistema',
      },
      {
        id: 'inf-k2',
        label: 'Deploy Frequency',
        value: '8/sem',
        trend: 'up',
        trendValue: '+2',
        description: 'Deploys por semana',
      },
      {
        id: 'inf-k3',
        label: 'MTTR',
        value: '12 min',
        trend: 'up',
        trendValue: '-5m',
        description: 'Tiempo medio de recuperación',
      },
      {
        id: 'inf-k4',
        label: 'Costo AWS',
        value: '$2.4k',
        trend: 'down',
        trendValue: '-18%',
        description: 'Costo mensual de infraestructura',
      },
    ],
  },
]

// ─── Legacy exports (backward compat) ────────────────────────────────────────
export const issues = projects[0].issues
export const backlogIssues = projects[0].backlog
export const currentSprint = projects[0].sprint
export const kpis = projects[0].kpis
